import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import PriceOverrideApiService from "@/core/services/api.service.price-override";

export default {
  name: "price-override-catalog",
  data() {
    return {
      // Filters
      filters: {
        shopId: null,
        name: "",
        sku: "",
        ean: ""
      },

      // Table data
      priceOverrides: [],
      totalItems: 0,
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10
      },

      // Service ready
      serviceReady: false,

      // Editing state
      editingItems: {},

      // CSV Import
      csvFile: null,
      csvShopId: null,
      importDialog: false,
      importLoading: false,

      // Import Results
      importResultsDialog: false,
      importResults: null,

      // Delete confirmation
      deleteDialog: false,
      itemToDelete: null,

      // Error details
      errorDialog: false,
      errorDetails: null,

      // Validation rules
      rules: {
        required: value => !!value || "Required",
        number: value => !isNaN(parseFloat(value)) || "Must be a number",
        positive: value => parseFloat(value) >= 0 || "Must be positive",
        decimal: value => {
          if (!value) return true;
          const regex = /^\d+(\.\d{1,2})?$/;
          return regex.test(value) || "Invalid format (e.g., 2.55)";
        }
      }
    };
  },

  computed: {
    headers() {
      return [
        { text: "ID", value: "id", sortable: false, width: "80px" },
        { text: "Image", value: "imageUrl", sortable: false, width: "80px" },
        { text: "Item Name", value: "itemName", sortable: false },
        { text: "SKU", value: "sku", sortable: false, width: "120px" },
        { text: "EAN", value: "ean", sortable: false, width: "120px" },
        { text: "Shop ID", value: "shopId", sortable: false, width: "100px" },
        { text: "Price", value: "price", sortable: false, width: "120px" },
        { text: "Purchase Price", value: "purchasePrice", sortable: false, width: "140px" },
        { text: "Target Price", value: "targetPrice", sortable: false, width: "130px" },
        { text: "Updated", value: "updatedAt", sortable: false, width: "160px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px" }
      ];
    },

    hasActiveFilters() {
      return (
        !!this.filters.shopId ||
        (this.filters.name && this.filters.name.trim() !== "") ||
        (this.filters.sku && this.filters.sku.trim() !== "") ||
        (this.filters.ean && this.filters.ean.trim() !== "")
      );
    }
  },

  mounted() {
    PriceOverrideApiService.init().then(() => {
      this.serviceReady = true;
      this.fetchPriceOverrides();
    });
  },

  watch: {
    pagination: {
      handler() {
        if (this.serviceReady) {
          this.fetchPriceOverrides();
        }
      },
      deep: true
    }
  },

  async created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Dashboard", route: "dashboard" },
      { title: "Price Override Catalog" }
    ]);
  },

  methods: {
    async fetchPriceOverrides() {
      this.loading = true;

      try {
        const params = {
          page: this.pagination.page - 1,
          size: this.pagination.itemsPerPage
        };

        if (this.filters.shopId) {
          params.shopId = this.filters.shopId;
        }

        if (this.filters.name && this.filters.name.trim()) {
          params.name = this.filters.name.trim();
        }

        if (this.filters.sku && this.filters.sku.trim()) {
          params.sku = this.filters.sku.trim();
        }

        if (this.filters.ean && this.filters.ean.trim()) {
          params.ean = this.filters.ean.trim();
        }

        const response = await PriceOverrideApiService.list(params);

        this.priceOverrides = response.data.content || [];
        this.totalItems = response.data.totalElements || 0;

        this.priceOverrides.forEach(item => {
          this.$set(this.editingItems, item.id, false);
        });
      } catch (error) {
        console.error("Error fetching price overrides:", error);
        this.$bvToast?.toast("Failed to fetch price overrides", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.pagination.page = 1;
      this.fetchPriceOverrides();
    },

    clearFilters() {
      this.filters.shopId = null;
      this.filters.name = "";
      this.filters.sku = "";
      this.filters.ean = "";
      this.pagination.page = 1;
      this.fetchPriceOverrides();
    },

    enableEdit(item) {
      this.$set(this.editingItems, item.id, true);
    },

    cancelEdit(item) {
      this.$set(this.editingItems, item.id, false);
      this.fetchPriceOverrides();
    },

    async saveEdit(item) {
      try {
        const updateData = {
          itemVariationId: item.variationId,
          price: parseFloat(item.price),
          purchasePrice: item.purchasePrice ? parseFloat(item.purchasePrice) : null,
          targetPrice: item.targetPrice ? parseFloat(item.targetPrice) : null
        };

        await PriceOverrideApiService.upsert(updateData);

        this.$set(this.editingItems, item.id, false);
        this.$bvToast?.toast("Product item price updated successfully", {
          title: "Success",
          variant: "success",
          solid: true
        });
        this.fetchPriceOverrides();
      } catch (error) {
        console.error("Error updating price override:", error);

        if (error.response && error.response.data) {
          this.errorDetails = error.response.data;
          this.errorDialog = true;
        } else {
          this.$bvToast?.toast("Failed to update price override", {
            title: "Error",
            variant: "danger",
            solid: true
          });
        }
      }
    },

    closeErrorDialog() {
      this.errorDialog = false;
      this.errorDetails = null;
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteItem() {
      if (!this.itemToDelete) return;

      try {
        await PriceOverrideApiService.delete(this.itemToDelete.id);
        this.$bvToast?.toast("Product item deleted successfully", {
          title: "Success",
          variant: "success",
          solid: true
        });
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.fetchPriceOverrides();
      } catch (error) {
        console.error("Error deleting product item:", error);
        this.$bvToast?.toast("Failed to delete product item", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      }
    },

    openImportDialog() {
      this.csvFile = null;
      this.csvShopId = null;
      this.importDialog = true;
    },

    onFileChange(file) {
      this.csvFile = file;
    },

    async importCsv() {
      if (!this.csvFile || !this.csvShopId) {
        this.$bvToast?.toast("Please select a file and enter shop ID", {
          title: "Warning",
          variant: "warning",
          solid: true
        });
        return;
      }

      this.importLoading = true;

      try {
        const response = await PriceOverrideApiService.importCsv(this.csvShopId, this.csvFile);
        this.importResults = response.data;
        this.importDialog = false;
        this.importResultsDialog = true;
        this.csvFile = null;
        this.csvShopId = null;
        this.fetchPriceOverrides();
      } catch (error) {
        console.error("Error importing CSV:", error);
        this.$bvToast?.toast("Failed to import CSV", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      } finally {
        this.importLoading = false;
      }
    },

    closeImportResults() {
      this.importResultsDialog = false;
      this.importResults = null;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    formatPrice(price) {
      if (price === null || price === undefined) return "-";
      return `€${parseFloat(price).toFixed(2)}`;
    },

    validateDecimalInput(event, item, field) {
      const value = event.target.value;
      // Allow only numbers and one dot
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(value)) {
        event.target.value = item[field];
      }
    }
  }
};
